// 화면 전체 줌 — html font-size 5단계 (rem 베이스). 사이드바 width 도 CSS 변수로 비례.
// 메시지 글자 크기 (data-font-size) 와 독립.
import { readSavedSize } from '@tpt/design';

export type UiScale = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const ALL: UiScale[] = ['sm', 'md', 'lg', 'xl', '2xl'];
const DEFAULT: UiScale = 'lg';
const KEY = 'tpt-ui-scale';

const USER_KEY = 'tpt-ui-scale-user'; // 사용자가 설정에서 직접 고른 값인지 표시 — 자동 기본값과 구분 (2026-07-12)

function isMobile(): boolean {
	try { return typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches; } catch { return false; }
}

export function readSavedUiScale(): UiScale {
	if (typeof localStorage === 'undefined') return DEFAULT;
	const v = localStorage.getItem(KEY);
	const userChosen = localStorage.getItem(USER_KEY) === '1';
	// 사용자가 직접 고른 값만 존중. (⚠️ 과거엔 boot 도 저장해 자동 'xl' 이 저장돼 있음 — user 마커 없으면 기본값 취급)
	if (userChosen && ALL.includes(v as UiScale)) return v as UiScale;
	// 📱 모바일 기본 = 아주크게 (2026-07-12 — "전반적으로 너무 작음" 피드백, 2026-07-09 'xl' 에서 한 단계 ↑)
	if (isMobile()) return '2xl';
	// 🔴 데스크톱은 user 마커 없는 저장값을 믿지 않는다 (2026-10-02 형 "2880×1800 등에서 메뉴/버튼 크기가 이상") —
	//   좁은 창(데스크톱 위젯 340px·팝아웃·줄인 웹 창)이 부팅하며 모바일 기본 '2xl' 을 저장했고, 그 값이 storage 이벤트로
	//   메인 창에 번지고 다음 부팅에도 박혀 데스크톱 전체가 '아주크게'가 됐다. 사용자 배율 조절은 zoom(--tpt-zoom) 축이다.
	return DEFAULT;
}

export function applyUiScale(s: UiScale, opts?: { user?: boolean }): void {
	if (typeof document === 'undefined') return;
	document.documentElement.setAttribute('data-ui-scale', s);
	// 💾 저장은 사용자가 직접 고른 경우만 — 부팅 기본값(창 폭으로 정해짐)을 저장하면 다른 창으로 번진다 (2026-10-02)
	if (opts?.user) {
		try { localStorage.setItem(KEY, s); localStorage.setItem(USER_KEY, '1'); } catch { /* private */ }
	}
	// 설정 화면(2축 독립 조절)에서 화면 배율만 바꿔도 프로필 메뉴 '글자 크기' 라벨이 동기화되도록 (2026-07-28)
	try { window.dispatchEvent(new CustomEvent(APP_SCALE_CHANGE_EVENT, { detail: s })); } catch { /* SSR */ }
}

export function bootUiScale(): void {
	applyUiScale(readSavedUiScale());
}

export const UI_SCALE_LABEL: Record<UiScale, string> = {
	sm: '작게',
	md: '조금작게',
	lg: '보통',
	xl: '크게',
	'2xl': '아주크게',
};

// ── 통합 배율 (웹 확대/축소식) — 화면(data-ui-scale)과 메시지 글자(data-font-size)를 한 번에 같은 단계로 (2026-07-23) ──
// UiScale 과 FontSize 는 리터럴이 동일('sm'|'md'|'lg'|'xl'|'2xl') 하므로 같은 단계로 동시 적용한다.
export const APP_SCALE_CHANGE_EVENT = 'tpt-app-scale-change';

// ── 🔎 미세 배율 zoom (2026-08-13 사용자 "볼륨 올리듯 100% 기본, 미세 조정") ──────────────
//   화면·메시지를 한 축으로 묶어 연속 조절한다. CSS 는 단계 세트의 기준값(-b)에 --tpt-zoom 을 곱한다(index.css).
//   기존 5단계(data-ui-scale)는 기기별 기본값(모바일 2xl / 데스크톱 lg)으로 남고, 사용자는 zoom 만 만진다.
//   → 메시지 글자만 따로 고르는 UI 는 폐기(사용자: "화면크기로만 동작되면 메시지 크기 버튼은 제외").
export const ZOOM_KEY = 'tpt-zoom';
export const ZOOM_MIN = 0.7;
export const ZOOM_MAX = 1.5;
export const ZOOM_STEP = 0.05;
export const ZOOM_DEFAULT = 1;
export const ZOOM_CHANGE_EVENT = 'tpt-zoom-change';

const clampZoom = (z: number): number => {
	if (!Number.isFinite(z)) return ZOOM_DEFAULT;
	// 5% 격자에 맞춰 반올림 — 슬라이더/휠/볼륨키가 모두 같은 값 집합을 쓰게
	const snapped = Math.round(z / ZOOM_STEP) * ZOOM_STEP;
	return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(snapped * 100) / 100));
};

export function readZoom(): number {
	if (typeof localStorage === 'undefined') return ZOOM_DEFAULT;
	const raw = localStorage.getItem(ZOOM_KEY);
	if (raw === null) return ZOOM_DEFAULT;
	const n = Number(raw);
	return Number.isFinite(n) ? clampZoom(n) : ZOOM_DEFAULT;
}

export function applyZoom(z: number): number {
	const v = clampZoom(z);
	if (typeof document !== 'undefined') document.documentElement.style.setProperty('--tpt-zoom', String(v));
	try { localStorage.setItem(ZOOM_KEY, String(v)); } catch { /* private */ }
	try { window.dispatchEvent(new CustomEvent(ZOOM_CHANGE_EVENT, { detail: v })); } catch { /* SSR */ }
	return v;
}

/** 부팅 시 1회 — 저장값을 html 에 반영. */
export function bootZoom(): void {
	applyZoom(readZoom());
}

/** A+/A− 한 칸(5%) 이동. 반환 = 이동 후 배율. */
export function bumpAppScale(delta: number): number {
	return applyZoom(readZoom() + delta * ZOOM_STEP);
}

/** 기본값(100%)으로 초기화. */
export function resetAppScale(): number {
	return applyZoom(ZOOM_DEFAULT);
}

// 🔎 배율 잠깐 표시 (2026-10-13 형 "웹처럼 휠로 조절할 때 %가 잠시 보였다 사라지게") — Ctrl+휠·Ctrl+=/−/0·안드 볼륨키처럼
//   **조작 중 현재 값이 안 보이는 경로**만 이 신호를 보낸다. 설정 슬라이더는 옆에 % 가 이미 있어 보내지 않는다(ZOOM_CHANGE_EVENT 와 분리한 이유).
//   표시는 components/common/ZoomIndicator (App 에 1회 마운트).
export const ZOOM_FLASH_EVENT = 'tpt-zoom-flash';
export function flashZoom(): void {
	try { window.dispatchEvent(new CustomEvent(ZOOM_FLASH_EVENT, { detail: zoomPercent() })); } catch { /* SSR */ }
}

/** 표시용 퍼센트 (예: 1.15 → 115) */
export function zoomPercent(z: number = readZoom()): number {
	return Math.round(z * 100);
}

// ── 창 간 배율 동기화 (2026-07-25) ──
// AI/캘린더/방 팝아웃(window.open·Tauri WebviewWindow)은 별도 document 라, 부모 창에서 배율을 바꿔도
// 그 창의 html 속성은 그대로였다. 같은 origin 의 다른 문서에서 localStorage 가 바뀌면 'storage' 이벤트가
// 오므로, 이를 받아 자기 문서에 재적용한다. (재적용은 setAttribute 만 — localStorage 를 다시 쓰지 않아 창 간 핑퐁 없음)
export function bootScaleSync(): void {
	if (typeof window === 'undefined') return;
	window.addEventListener('storage', (e) => {
		const html = document.documentElement;
		// e.key === null: clear() 등 — 전체 재적용
		if (e.key === null || e.key === 'tpt-ui-scale' || e.key === 'tpt-ui-scale-user') {
			html.setAttribute('data-ui-scale', readSavedUiScale());
		}
		if (e.key === null || e.key === 'tpt-font-size') {
			html.setAttribute('data-font-size', readSavedSize());
		}
		// 🔎 미세 배율도 창 간 동기 (2026-08-13)
		if (e.key === null || e.key === ZOOM_KEY) {
			html.style.setProperty('--tpt-zoom', String(readZoom()));
		}
	});
}

// 🔊 안드 앱 볼륨키 용도 (2026-10-13 형 "설정에서 볼륨 조절로 할 수 있도록 토글 — 기본은 화면크기") — 네이티브(code 122+)가 값을 들고
//   prefs 에 남긴다(웹이 뜨기 전에도 같은 동작). 웹은 토글만. 브릿지가 없으면(구 앱·iOS·데탑·웹) 토글 자체를 숨긴다.
//   🔴 브릿지 메서드는 반드시 .call(bridge, …) — 언바인드 호출 함정(GUIDE).
export type VolumeKeyMode = 'zoom' | 'volume';
export function volumeKeyModeAvailable(): boolean {
	const b = typeof window !== 'undefined' ? window.TPTAndroid : undefined;
	return !!b && typeof b.setVolumeKeyMode === 'function' && typeof b.getVolumeKeyMode === 'function';
}
export function getVolumeKeyMode(): VolumeKeyMode {
	const b = typeof window !== 'undefined' ? window.TPTAndroid : undefined;
	try { return b?.getVolumeKeyMode && b.getVolumeKeyMode.call(b) === 'volume' ? 'volume' : 'zoom'; } catch { return 'zoom'; }
}
export function setVolumeKeyMode(mode: VolumeKeyMode): void {
	const b = typeof window !== 'undefined' ? window.TPTAndroid : undefined;
	try { b?.setVolumeKeyMode?.call(b, mode); } catch { /* 구 앱 — 무시 */ }
}
